import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Bell, Plus, Trash2, Gauge, Globe, SlidersHorizontal } from "lucide-react";
import AlertCard from "@/components/AlertCard";

export default function SpendAlerts({ thresholds = [], alerts = [], categories = [], budgetCategories = [], unread = 0, onReload, onManage }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [limit, setLimit] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!category || !limit) return;
    setSaving(true);
    try {
      await api.post("/spend-alerts/thresholds", { category, limit: toGbp(limit) });
      toast.success(`Threshold set for ${category}`);
      setOpen(false); setCategory(""); setLimit("");
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, cat) => {
    try {
      await api.delete(`/spend-alerts/thresholds/${id}`);
      toast.success(`Removed ${cat} threshold`);
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/spend-alerts/${id}/read`);
      onReload?.();
    } catch {}
  };

  return (
    <div id="spend-alerts-section" className="mm-glow-card p-6 h-full" data-testid="spend-alerts">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Spend alerts</p>
          <h3 className="font-display text-lg font-semibold mt-1">Category budgets</h3>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="manage-budgets-button" onClick={onManage} className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0D0D0D] px-3 py-1.5 text-xs text-slate-300 hover:border-[#00C896]/40 transition">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Manage
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-threshold-button" size="sm" className="rounded-full bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">
                <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Set limit</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-[#14181D] border-white/10 text-white" data-testid="threshold-modal">
            <DialogHeader><DialogTitle className="font-display">Set a spending limit</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-slate-300 text-sm">Budget type</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="threshold-category-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white">
                    <SelectValue placeholder="Overall or a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14181D] border-white/10 text-white">
                    {(budgetCategories.length ? budgetCategories : categories.filter((c) => c !== "Income")).map((c) => (
                      <SelectItem key={c} value={c} className="focus:bg-[#00C896]/20">{c === "Overall" ? "Overall monthly limit" : c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Monthly limit ({curSymbol()})</Label>
                <Input
                  data-testid="threshold-limit-input"
                  type="number" min="1" step="1"
                  value={limit} onChange={(e) => setLimit(e.target.value)}
                  placeholder="250"
                  className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]"
                />
              </div>
              <Button data-testid="save-threshold-button" onClick={save} disabled={saving || !category || !limit} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">
                Save limit
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {thresholds.map((t) => {
          const eff = t.effective_limit || t.limit;
          const pct = Math.min(100, (t.spent / eff) * 100);
          const over = t.spent > eff;
          const near = !over && pct >= (t.warn_percent || 80);
          const color = over ? "#f43f5e" : near ? "#f59e0b" : "#00C896";
          return (
            <div key={t.id} data-testid={`threshold-${t.category}`} className="group rounded-xl border border-white/8 bg-[#0D0D0D] p-3.5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {t.category === "Overall" ? <Globe className="h-3.5 w-3.5 text-sky-400" /> : <Gauge className="h-3.5 w-3.5 text-slate-500" />}
                  <span className="text-sm font-medium">{t.category === "Overall" ? "Overall monthly" : t.category}</span>
                  {over && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-300">Over</span>}
                  {near && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300">{t.warn_percent || 80}%</span>}
                  {t.rollover && t.rollover_amount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#00C896]/30 bg-[#00C896]/10 text-[#4DE3BF]">+{gbp(t.rollover_amount)}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-num text-xs text-slate-400">{gbp(t.spent)} / {gbp(eff)}</span>
                  <button data-testid={`delete-threshold-${t.category}`} onClick={() => remove(t.id, t.category)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
        {thresholds.length === 0 && <p className="text-sm text-slate-500">No limits set. Add one to start getting alerts.</p>}
      </div>

      <div className="mt-5 pt-4 border-t border-white/8">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-4 w-4 text-[#00C896]" />
          <span className="text-sm font-medium">Alert feed</span>
          {unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white font-bold">{unread} new</span>}
        </div>
        <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
          {alerts.map((a) => (
            <AlertCard key={a.id} alert={a} onRead={markRead} compact />
          ))}
          {alerts.length === 0 && <p className="text-sm text-slate-500">No alerts yet. When a transaction pushes a category over its limit, it shows up here (and in your inbox).</p>}
        </div>
      </div>
    </div>
  );
}
